/**
 * PricingRuleCreatePreview — Type-specific preview panels for the create wizard.
 *
 * Shows a live preview that updates as the user configures their pricing rule:
 *   - PriceDiscountPreview: PDP mockup with discount badge
 *   - CouponPreview: Shopee-style coupon cards
 *   - FreeItemPreview: Buy X → Get Y visual
 *   - DefaultPreview: Basic PDP (no type selected)
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Gift,
  ArrowRight,
  Star,
  ShoppingBag,
  Heart,
} from "lucide-react";

// ── Shared state shape for previews ────────────────────────

export interface PreviewState {
  ruleType: "price" | "coupon" | "product" | null;
  title: string;
  discountMode: "percentage" | "amount" | "rate" | null;
  discountValue: number;
  sameItem: boolean;
  freeItem: string;
  freeQty: number;
  applyOn: string;
  minQty: string;
  minAmt: string;
  validUpto: string;
}

// ── Price Discount Preview (PDP mockup) ────────────────────

function PriceDiscountPreview({ state }: { state: PreviewState }) {
  const originalPrice = 1990;
  const discounted = useMemo(() => {
    if (!state.discountMode || state.discountValue === 0) return originalPrice;
    if (state.discountMode === "percentage") return originalPrice * (1 - state.discountValue / 100);
    if (state.discountMode === "amount") return originalPrice - state.discountValue;
    if (state.discountMode === "rate") return state.discountValue;
    return originalPrice;
  }, [state.discountMode, state.discountValue]);

  const hasDiscount = state.discountValue > 0 && state.discountMode;
  const savings = originalPrice - Math.max(0, discounted);

  return (
    <div className="rounded-md border border-border bg-background overflow-hidden shadow-sm">
      <div className="h-5 bg-foreground/5 flex items-center justify-center">
        <div className="w-14 h-1 rounded-full bg-foreground/20" />
      </div>
      <div className="aspect-[4/3] relative flex items-center justify-center bg-primary/[0.03]">
        <ShoppingBag className="w-12 h-12 text-muted-foreground/30" />
        {hasDiscount && (
          <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-primary-foreground text-[10px] font-semibold bg-primary animate-in fade-in slide-in-from-left-2">
            {state.discountMode === "percentage" && `-${state.discountValue}%`}
            {state.discountMode === "amount" && `-฿${state.discountValue.toLocaleString()}`}
            {state.discountMode === "rate" && `฿${state.discountValue.toLocaleString()}`}
          </div>
        )}
        <button className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center">
          <Heart className="w-3 h-3 text-muted-foreground" />
        </button>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className={cn("w-2.5 h-2.5", i <= 4 ? "text-amber-400 fill-amber-400" : "text-muted")} />
          ))}
          <span className="text-[10px] text-muted-foreground ml-1">4.0</span>
        </div>
        <p className="font-medium text-xs">Premium Cotton T-Shirt</p>
        <div className="flex items-baseline gap-1.5">
          <span className={cn("text-base font-semibold tabular-nums", hasDiscount && "text-primary")}>
            ฿{Math.max(0, Math.round(discounted)).toLocaleString()}
          </span>
          {hasDiscount && (
            <>
              <span className="text-[10px] text-muted-foreground line-through tabular-nums">฿{originalPrice.toLocaleString()}</span>
              <span className="text-[9px] font-medium px-1 py-0.5 rounded bg-primary/10 text-primary">
                -{Math.round(savings).toLocaleString()}
              </span>
            </>
          )}
        </div>
        <Button className="w-full h-8 rounded text-xs font-medium">Add to Bag</Button>
      </div>
    </div>
  );
}

// ── Coupon Preview (Shopee-style) ──────────────────────────

function CouponPreview({ state }: { state: PreviewState }) {
  const discountLabel = state.discountMode === "percentage"
    ? `${state.discountValue || 0}%`
    : `฿${(state.discountValue || 0).toLocaleString()}`;
  const hasValue = state.discountValue > 0 && state.discountMode;

  return (
    <div className="rounded-md border border-border bg-background overflow-hidden shadow-sm">
      <div className="h-5 bg-foreground/5 flex items-center justify-center">
        <div className="w-14 h-1 rounded-full bg-foreground/20" />
      </div>
      <div className="px-3 pt-3 pb-2">
        <p className="text-[11px] font-semibold text-foreground">My Coupons</p>
        <p className="text-[9px] text-muted-foreground">Collect & use at checkout</p>
      </div>
      <div className="px-3 pb-3 space-y-2">
        {/* Active coupon */}
        <div className="flex rounded border border-primary/30 bg-primary/[0.04] overflow-hidden animate-in fade-in slide-in-from-bottom-2">
          <div className="w-[72px] flex flex-col items-center justify-center bg-primary px-2 py-3 relative">
            <div className="absolute top-0 bottom-0 right-0 w-px border-r border-dashed border-primary-foreground/30" />
            <div className="absolute -right-1.5 top-3 w-3 h-3 rounded-full bg-background" />
            <div className="absolute -right-1.5 bottom-3 w-3 h-3 rounded-full bg-background" />
            <span className="text-lg font-bold text-primary-foreground tabular-nums leading-none">
              {hasValue ? discountLabel : "??"}
            </span>
            <span className="text-[8px] text-primary-foreground/80 font-medium mt-0.5">OFF</span>
          </div>
          <div className="flex-1 px-2.5 py-2.5 flex flex-col justify-between">
            <div>
              <p className="text-[10px] font-semibold text-foreground leading-tight">
                {state.title || "Coupon Discount"}
              </p>
              <p className="text-[8px] text-muted-foreground mt-0.5">
                {state.applyOn === "Transaction" ? "All products" : state.applyOn || "All products"}
              </p>
              {state.minAmt && (
                <p className="text-[8px] text-muted-foreground">Min. spend ฿{parseInt(state.minAmt).toLocaleString()}</p>
              )}
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[7px] text-muted-foreground">
                {state.validUpto ? `Expires ${state.validUpto}` : "No expiry"}
              </span>
              <div className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[8px] font-semibold">
                Collect
              </div>
            </div>
          </div>
        </div>
        {/* Inactive examples */}
        {[
          { discount: "฿50", title: "Welcome Gift", min: "฿500", expires: "May 31" },
          { discount: "10%", title: "Member Exclusive", min: "฿300", expires: "Jun 15" },
        ].map((c, i) => (
          <div key={i} className="flex rounded border border-border overflow-hidden opacity-50">
            <div className="w-[72px] flex flex-col items-center justify-center bg-muted px-2 py-3 relative">
              <div className="absolute top-0 bottom-0 right-0 w-px border-r border-dashed border-muted-foreground/20" />
              <div className="absolute -right-1.5 top-3 w-3 h-3 rounded-full bg-background" />
              <div className="absolute -right-1.5 bottom-3 w-3 h-3 rounded-full bg-background" />
              <span className="text-sm font-bold text-muted-foreground tabular-nums">{c.discount}</span>
              <span className="text-[8px] text-muted-foreground/60 font-medium mt-0.5">OFF</span>
            </div>
            <div className="flex-1 px-2.5 py-2.5">
              <p className="text-[10px] font-medium text-muted-foreground">{c.title}</p>
              <p className="text-[8px] text-muted-foreground/60">Min. spend {c.min}</p>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[7px] text-muted-foreground/60">Expires {c.expires}</span>
                <div className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[8px] font-medium">
                  Collect
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Free Item Preview (Buy X Get Y) ───────────────────────

function FreeItemPreview({ state }: { state: PreviewState }) {
  return (
    <div className="rounded-md border border-border bg-background overflow-hidden shadow-sm">
      <div className="h-5 bg-foreground/5 flex items-center justify-center">
        <div className="w-14 h-1 rounded-full bg-foreground/20" />
      </div>
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <Gift className="w-3.5 h-3.5 text-primary" />
          <span className="text-[11px] font-semibold text-primary">
            {state.title || "Buy & Get Free"}
          </span>
        </div>
      </div>
      <div className="px-3 py-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 text-center">
            <div className="aspect-square rounded border border-border bg-muted/20 flex items-center justify-center mb-1.5">
              <ShoppingBag className="w-8 h-8 text-muted-foreground/30" />
            </div>
            <div className="px-2 py-1 rounded-full bg-foreground/5">
              <p className="text-[9px] font-semibold text-foreground">BUY</p>
              <p className="text-[8px] text-muted-foreground">
                {state.minQty ? `${state.minQty} items` : "1 item"}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
              <ArrowRight className="w-3 h-3 text-primary-foreground" />
            </div>
            <span className="text-[7px] font-semibold text-primary uppercase">Free</span>
          </div>
          <div className="flex-1 text-center">
            <div className="aspect-square rounded border-2 border-dashed border-primary/40 bg-primary/[0.03] flex items-center justify-center mb-1.5 relative">
              <Gift className="w-8 h-8 text-primary/30" />
              <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <span className="text-[8px] font-bold text-primary-foreground">x{state.freeQty}</span>
              </div>
            </div>
            <div className="px-2 py-1 rounded-full bg-primary/10">
              <p className="text-[9px] font-semibold text-primary">GET FREE</p>
              <p className="text-[8px] text-primary/70">
                {state.sameItem ? "Same item" : state.freeItem || "Selected item"}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-3 pb-3 space-y-1.5">
        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">How it works</p>
        {[
          { step: "1", text: `Add ${state.minQty || "1"} qualifying item${(parseInt(state.minQty) || 1) > 1 ? "s" : ""} to cart` },
          { step: "2", text: `${state.freeQty} free item${state.freeQty > 1 ? "s" : ""} added automatically` },
          { step: "3", text: "Discount applied at checkout" },
        ].map((s) => (
          <div key={s.step} className="flex items-start gap-2">
            <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[8px] font-semibold text-primary">{s.step}</span>
            </div>
            <p className="text-[9px] text-muted-foreground leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>
      <div className="px-3 pb-3">
        <Button className="w-full h-8 rounded text-xs font-medium">Shop Now</Button>
      </div>
    </div>
  );
}

// ── Default Preview ────────────────────────────────────────

function DefaultPreview() {
  return (
    <div className="rounded-md border border-border bg-background overflow-hidden shadow-sm">
      <div className="h-5 bg-foreground/5 flex items-center justify-center">
        <div className="w-14 h-1 rounded-full bg-foreground/20" />
      </div>
      <div className="aspect-[4/3] relative flex items-center justify-center bg-muted/30">
        <ShoppingBag className="w-12 h-12 text-muted-foreground/30" />
        <button className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center">
          <Heart className="w-3 h-3 text-muted-foreground" />
        </button>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className={cn("w-2.5 h-2.5", i <= 4 ? "text-amber-400 fill-amber-400" : "text-muted")} />
          ))}
          <span className="text-[10px] text-muted-foreground ml-1">4.0</span>
        </div>
        <p className="font-medium text-xs">Premium Cotton T-Shirt</p>
        <p className="text-base font-semibold tabular-nums">฿1,990</p>
        <Button className="w-full h-8 rounded text-xs font-medium">Add to Bag</Button>
      </div>
    </div>
  );
}

// ── Preview Router ─────────────────────────────────────────

export function PreviewPanel({ state }: { state: PreviewState }) {
  return (
    <div className="sticky top-10 space-y-4">
      {state.ruleType === "price" && <PriceDiscountPreview state={state} />}
      {state.ruleType === "coupon" && <CouponPreview state={state} />}
      {state.ruleType === "product" && <FreeItemPreview state={state} />}
      {!state.ruleType && <DefaultPreview />}
      <p className="text-[10px] text-muted-foreground text-center">
        Live preview — updates as you configure
      </p>
    </div>
  );
}
